/**
 * `Docs_Executor.ts` — port of Unity's `AI/Executor/PDF_Executor.cs`
 * (Knowledge tool family). See `Docs_Registry.ts` for the file-name note.
 *
 * Each tool publishes what it searched and the SOURCE it used
 * (`"string search"` / `"semantic search"` / `"llm"`) to the conversation
 * presentation, then attaches any citations, exactly as Unity does — that is
 * what makes an answer traceable back to a document.
 *
 * `Save_PDF_placement` has no web counterpart (no in-app PDF panel) and is
 * not registered, so it never reaches this dispatcher.
 *
 * The last three cases are Spark Preview stubs — everything the spec wants to
 * happen INSIDE an open document. The web opens documents in a browser tab, so
 * there is nothing to highlight in, step through, or search. See
 * `previewNotImplemented.ts`.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { IAIContextEngineModel } from "@ai/context/AIContextEngineModel.Core";
import {
  K_EMPTY_PART_HIERARCHY_CONTEXT,
  type AIFileCitation,
} from "@ai/context/AIContextEngineModel.KnowledgeTransport";
import { previewNotImplemented } from "./previewNotImplemented";
import { toChipCitations, withDocumentDisplayNames } from "./citationDisplayNames";
import { agentFacingDocumentName } from "@models/docs/documentDisplayNames";
import {
  getString,
  type AgentToolExecutorHost,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class DocsExecutor implements IDomainExecutor {
  constructor(
    private readonly m_contextEngine: IAIContextEngineModel,
    private readonly m_host: AgentToolExecutorHost,
  ) {}

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.PromptSearchDocs:
        return this.promptSearchDocsAsync(args);
      case AgentToolNames.SearchDocByFileName:
        return this.searchDocByFileNameAsync(args);
      case AgentToolNames.FindAndShowPDF:
        return this.findAndShowPdfAsync(args);
      case AgentToolNames.OpenDocAtReference:
        return previewNotImplemented(
          "Opening a document at the exact passage, highlighted",
          `opening the whole document with ${AgentToolNames.FindAndShowPDF}, and reading the passage out`,
        );
      case AgentToolNames.GoToNextDocResult:
        return previewNotImplemented("Stepping through matches inside an open document");
      case AgentToolNames.SearchInOpenDoc:
        return previewNotImplemented(
          "Searching inside the document the user has open",
          `searching every project document with ${AgentToolNames.PromptSearchDocs}`,
        );
      default:
        return null;
    }
  }

  /** Unity `PromptSearchDocsAsync` (`PDF_Executor.cs:45-70`). */
  private async promptSearchDocsAsync(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt");
    if (!prompt) return "error: prompt_required";

    const docsPrompt = this.m_contextEngine.buildDocsPrompt(prompt.trim());
    const presentation = this.m_host.conversationPresentation;
    presentation?.publishToolUsagePrompt(AgentToolNames.PromptSearchDocs, docsPrompt);
    presentation?.publishToolUsageSource(AgentToolNames.PromptSearchDocs, "semantic search");

    const response = await this.m_contextEngine.queryKnowledgeContextAsync(
      docsPrompt,
      K_EMPTY_PART_HIERARCHY_CONTEXT,
      // `semanticOnly` — this tool exists for CONTENT questions, so the cheap
      // manifest pre-pass that answers "which file is it" is skipped.
      true,
    );
    this.m_host.trackHelperAgentUsage?.("Knowledge Context Agent", response?.usage ?? null);
    // `sources`, not `citations`: the sub-agent annotates the document it quoted
    // and routinely leaves the others unmarked, so the cited set understates
    // what the search actually matched.
    //
    // THE `searchRan` TEST IS LOAD-BEARING, and the plain `??` it replaces was
    // a real leak. `sources` is null for two different reasons: no search ran
    // (the manifest paths, which this fallback is FOR), or a search ran and
    // matched nothing above the relevance floor. In the second case `citations`
    // is - with no annotations - the raw unfiltered hit list, so `?? citations`
    // republished to the user the exact set the floor had just rejected, and
    // appended their names to the text the main agent reads as `Sources (n)`.
    // A search that RAN is authoritative, including when it found nothing.
    const sources = response?.searchRan
      ? response.sources ?? null
      : response?.sources ?? response?.citations ?? null;
    // Resolve titles ONCE and use the same array for both consumers: the chips
    // collapse to the title alone, the `Sources (n)` line keeps both names. They
    // must not be resolved separately — two lookups is two chances to disagree
    // about what the user is looking at.
    const labelled = (await withDocumentDisplayNames(this.m_host, sources)) ?? sources;
    if (labelled?.length) presentation?.publishCitations(toChipCitations(labelled));
    if (!response?.text) return "error: knowledge_context_agent_parse_failed";
    return withSources(response.text, labelled);
  }

  /** Unity `SearchDocByFileNameAsync` (`:72-99`). Manifest only — never a model call. */
  private async searchDocByFileNameAsync(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt");
    if (!prompt) return "error: prompt_required";

    const trimmed = prompt.trim();
    const presentation = this.m_host.conversationPresentation;
    presentation?.publishToolUsagePrompt(AgentToolNames.SearchDocByFileName, trimmed);
    presentation?.publishToolUsageSource(AgentToolNames.SearchDocByFileName, "string search");

    const response = await this.m_contextEngine.searchDocsByFileNameAsync(trimmed);
    this.m_host.trackHelperAgentUsage?.("Knowledge Context Agent", response?.usage ?? null);
    const sources = response?.sources ?? response?.citations ?? null;
    // One resolve, two consumers - see the note at the sibling call above.
    const labelled = (await withDocumentDisplayNames(this.m_host, sources)) ?? sources;
    if (labelled?.length) presentation?.publishCitations(toChipCitations(labelled));
    if (!response?.text) return "error: knowledge_context_agent_parse_failed";
    return withSources(response.text, labelled);
  }

  /** Unity `FindAndShowPdfAsync` (`:101-165`). */
  private async findAndShowPdfAsync(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt");
    if (!prompt) return "error: prompt_required";

    const presentation = this.m_host.conversationPresentation;
    if (!presentation) return "error: ui_unavailable";

    const trimmed = prompt.trim();
    presentation.publishToolUsagePrompt(AgentToolNames.FindAndShowPDF, trimmed);

    const response = await this.m_contextEngine.findPdfAsync(trimmed);
    this.m_host.trackHelperAgentUsage?.("Knowledge Context Agent", response?.usage ?? null);
    presentation.publishToolUsageSource(
      AgentToolNames.FindAndShowPDF,
      response?.usedStringSearch ? "string search" : "llm",
    );

    if (!response) return "error: pdf_not_found";
    if (response.text?.startsWith("error:")) return response.text.trim();
    if (!response.citations || response.citations.length === 0) {
      return response.text?.trim() || "error: pdf_not_found";
    }
    // Ambiguity is reported back so the agent can ASK, rather than guessing —
    // the slot-by-slot clarification policy in `Campfire_AI_Harness.md`.
    if (response.citations.length !== 1) {
      return response.text?.trim() || "error: multiple_pdf_matches";
    }

    const chosen = response.citations[0];
    const storagePath =
      chosen.storagePath || (chosen.fileName ? `docs/${chosen.fileName}` : null);
    if (!storagePath) return "error: pdf_not_found";

    // ⚠ THE CHIP IS THE AFFORDANCE — this tool SHOWS a reference and never opens
    // a tab itself (user direction 2026-09-07). An agent tool call carries no
    // transient user activation, so the post-fetch `window.open` it used to make
    // is refused by default in Chrome; and because this was the one doc handler
    // that published no citation of its own, that refusal left no tab, no chip
    // and a dead-end sentence. `useSpark.openCitation` awaits nothing before it
    // reserves its tab, so a TAP still carries activation and opens reliably.
    //
    // The chip carries the Define title (`withDocumentDisplayNames`) like both
    // sibling handlers, and `useSpark` de-duplicates on `storagePath` — so a turn
    // that also ran `Search_Doc_By_File_Name` still shows exactly one.
    const labelled = (await withDocumentDisplayNames(this.m_host, [chosen])) ?? [chosen];
    presentation.publishCitations(toChipCitations(labelled));

    // Both names, like the `Sources (n)` line above: the title is what the agent
    // SPEAKS, the file name is what it matches on if the user names a document
    // next (user direction 2026-09-04, extended 2026-09-21).
    const label = agentFacingDocumentName(labelled[0].displayName, chosen.fileName)
      || storagePath;
    return `ok: found ${label}`;
  }
}

/**
 * The answer convention asks every document answer to say how many sources it
 * found and to offer them all. The sources already reach the panel through
 * `publishCitations`, but the AGENT only ever saw the prose — so it could not
 * say "three sources" or name the runners-up for the user to pick from.
 *
 * Takes the RANKED source list, so the count here and the chips on screen are
 * always the same set. Passing the cited subset told the agent one document
 * existed when the search had matched several.
 */
function withSources(
  text: string,
  citations: ReadonlyArray<AIFileCitation> | null,
): string {
  const trimmed = text.trim();
  if (trimmed.startsWith("error:")) return trimmed;

  // Identity is the storage path (falling back to the file id), matching
  // `publishCitations`. Keying on the DISPLAY NAME instead would silently merge
  // two different documents that happen to share a file name, so the count here
  // could disagree with the chips the user is looking at.
  const seen = new Set<string>();
  const names: string[] = [];
  for (const citation of citations ?? []) {
    const key = (citation.storagePath?.trim() || citation.fileId?.trim() || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    // Title AND file name — the title so the agent has a speakable name, the
    // file name so it keeps the specific word it matches a document on.
    const name =
      agentFacingDocumentName(citation.displayName, citation.fileName)
      || citation.storagePath?.trim();
    if (name) names.push(name);
  }
  if (names.length === 0) return trimmed;
  return `${trimmed}\n\nSources (${names.length}): ${names.join(", ")}`;
}
